/* eslint-disable @typescript-eslint/no-empty-object-type */
/* eslint-disable @typescript-eslint/no-unused-vars */
// 1) Third-party library imports
import {
    get,
    keyBy,
    merge,
    uniqWith,
    isEqual,
    values
} from 'lodash-es';
import {
    createAction,
    createSelector,
    createSlice,
    PayloadAction
} from '@reduxjs/toolkit';

// 2) Local imports - Constants
import {
    CUSTOMERS_PAGE,
    DETAILS_TAB,
} from '../../config/tableAndPageConstants';
import { conversationTableFilterOptions } from '../../constants/common';
import {
    creditsSortByOptions,
    creditsStateFilterOptions,
    rebatesSortByOptions,
    rebatesStateFilterOptions,
} from '../../constants/creditsSortAndFilters';
import {
    contactSelectType,
    customersATBCalculationOptions,
    customersSortByOptionsMap,
    customersStateFilterOptions
} from '../../constants/customersSortAndFilters';
import {
    invoicesSortByOptions,
    invoicesStateFilterOptions,
} from '../../constants/invoicesSortAndFilters';
import {
    paymentsCommonSortByOptions,
    paymentsStateFilterOptions
} from '../../constants/paymentsSortAndFilters';

// 3) Local imports - Project-specific modules / types
import {
    BulkSaveCustomerUpdateRequestPayload,
    Customer,
    CustomerAddCommentRequestPayload,
    CustomerAssignUserRequestPayload,
    CustomerSendInformationRequestPayload,
    CustomersFetchedForOrganisationPayload,
    CustomersState,
    GetCustomersRequestPayload,
    SaveCustomerUpdateRequestPayload
} from './types';
import {
    Conversation,
    GetConversationRequestPayload,
    PageData
} from '../common/types';
import {
    GetInvoicesRequestPayload,
    Invoice,
    InvoicesFetchedForOrganisationPayload
} from '../invoices/types';
import {
    Credit,
    CreditsFetchedForOrganisationPayload,
    GetCreditsRequestPayload
} from '../credits/types';
import {
    GetPaymentsRequestPayload,
    Payment,
    PaymentsFetchedForOrganisationPayload
} from '../payments/types';
import { ConversationLinesFetchedForOrganisationPayload } from '../conversationLine/types';
import type { RootState } from '../storeTypes';

// 4) Local imports - Utility functions
import { DynamicObject } from '../../utils/commonInterfaces';

const actionPrefix = 'customer';

// Type-safe initialState!
export const initialState: CustomersState = {
    loading: false,
    saveCustomerDataLoading: false,
    errorMessages: [],
    data: [],
    pageData: {
        pageSize: CUSTOMERS_PAGE.pageSize,
        currentPage: 0,
        hasNextPage: false,
    },
    filters: {
        Customer: '',
        InclusiveContact:
            get(contactSelectType, '0.value') === 'true' ? true : false,
        Contact: 0,
        AmountType: '',
        AmountValue: '',
        AmountOperator: '',
    },
    tableFilter: undefined,
    atbType: get(customersATBCalculationOptions, `${0}.value`),
    sortBy: customersSortByOptionsMap.customerName.value, // for Company Name
    sortAscending: true,
    stateOfCustomers: get(customersStateFilterOptions, `${0}.value`),
    activeData: {
        record: {},
        children: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.CUSTOMER_LIST_COMPLETE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {
                Customer: '',
                InclusiveContact:
                    get(contactSelectType, '0.value') === 'true' ? true : false,
                Contact: 0,
                AmountType: '',
                AmountValue: '',
                AmountOperator: '',
            },
            sortBy: customersSortByOptionsMap.customerName.value, // 2 for Invoice number
            sortAscending: true,
        },
        invoices: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.INVOICE_LIST_COMPLETE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {
                InvoiceNumber: '',
                Customer: '',
                CustomerCode: '',
                CreatedDateMin: '',
                CreatedDateMax: '',
                DueDateMin: '',
                DueDateMax: '',
                AmountType: '',
                AmountValue: '',
                AmountOperator: '',
            },
            invoiceState: get(invoicesStateFilterOptions, `${0}.value`),
            sortBy: invoicesSortByOptions[0].value, // 2 for Invoice number
            sortAscending: true,
        },
        credits: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.CREDIT_LIST_COMPLETE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            creditState: get(creditsStateFilterOptions, `${0}.value`),
            sortBy: creditsSortByOptions[0].value,
            sortAscending: true,
        },
        rebates: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.CREDIT_LIST_COMPLETE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            creditState: get(rebatesStateFilterOptions, `${0}.value`),
            sortBy: rebatesSortByOptions[0].value,
            sortAscending: true,
        },
        payments: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.PAYMENT_LIST_COMPLETE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            sortBy: paymentsCommonSortByOptions[1].value,
            sortAscending: false,
            paymentState: get(paymentsStateFilterOptions, `${0}.value`)
        },
        conversation: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.CONVERSATION_TIMELINE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            conversationTableFilter: get(
                conversationTableFilterOptions,
                '0.value'
            ),
            sortBy: 'Created Date Time',
            sortAscending: false,
        },
        loading: false,
        selectedId: null,
        errorMessages: [],
    },
    parentActiveData: {
        record: {},
        children: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.CUSTOMER_LIST_COMPLETE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {
                Customer: '',
                InclusiveContact:
                    get(contactSelectType, '0.value') === 'true' ? true : false,
                Contact: 0,
                AmountType: '',
                AmountValue: '',
                AmountOperator: '',
            },
            sortBy: customersSortByOptionsMap.customerName.value, // 2 for Invoice number
            sortAscending: true,
        },
        invoices: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.INVOICE_LIST_COMPLETE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {
                InvoiceNumber: '',
                Customer: '',
                CustomerCode: '',
                CreatedDateMin: '',
                CreatedDateMax: '',
                DueDateMin: '',
                DueDateMax: '',
                AmountType: '',
                AmountValue: '',
                AmountOperator: '',
            },
            invoiceState: get(invoicesStateFilterOptions, `${0}.value`),
            sortBy: invoicesSortByOptions[0].value, // 2 for Invoice number
            sortAscending: true,
        },
        credits: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.CREDIT_LIST_COMPLETE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            creditState: get(creditsStateFilterOptions, `${0}.value`),
            sortBy: creditsSortByOptions[0].value,
            sortAscending: true,
        },
        rebates: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.CREDIT_LIST_COMPLETE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            creditState: get(rebatesStateFilterOptions, `${0}.value`),
            sortBy: rebatesSortByOptions[0].value,
            sortAscending: true,
        },
        payments: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.PAYMENT_LIST_COMPLETE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            sortBy: paymentsCommonSortByOptions[1].value,
            sortAscending: false,
        },
        conversation: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.CONVERSATION_TIMELINE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            conversationTableFilter: get(
                conversationTableFilterOptions,
                '0.value'
            ),
            sortBy: 'Created Date Time',
            sortAscending: false,
        },
        loading: false,
        selectedId: null,
        errorMessages: [],
    }
};

// Thanks to Redux 4's much simpler typings, we can take away a lot of typings on the reducer side,
// everything will remain type-safe.
const customerSlice = createSlice({
    name: actionPrefix,
    initialState,
    reducers: {
        getCustomersForOrganisationRequestAction: (
            state,
            _action: PayloadAction<GetCustomersRequestPayload>
        ) => {
            state.loading = true;
        },
        getCustomersRequestAction: (
            state,
            _action: PayloadAction<GetCustomersRequestPayload>
        ) => {
            state.loading = true;
        },
        getCustomersForPaymentPlansRequestAction: (
            state,
            _action: PayloadAction<GetCustomersRequestPayload>
        ) => {
            state.loading = true;
        },
        getCustomersSuccessAction: (
            state,
            action: PayloadAction<{
                data: Customer[];
                pageData: PageData;
                mergeData?: boolean;
            }>
        ) => {
            if (action.payload.mergeData === true) {
                state.data = values(
                    merge(
                        keyBy(state.data, 'Id'),
                        keyBy(action.payload.data, 'Id')
                    )
                );
            } else if (action.payload.pageData.currentPage === 0) {
                state.data = action.payload.data;
            } else {
                const existingData = state.data || [];
                state.data = uniqWith([...existingData, ...action.payload.data], isEqual);
            }

            state.loading = false;
            state.pageData = action.payload.pageData;
            state.errorMessages = [];
        },
        getCustomersErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.loading = false;
            state.data = [];
            state.errorMessages = action.payload;
        },
        updateCustomersFiltersAction: (
            state,
            action: PayloadAction<GetCustomersRequestPayload['filters']>
        ) => {
           state.filters = action.payload;
        },
        updateCustomersSortByAndStateAction: (
            state,
            action: PayloadAction<{
                sortBy: GetCustomersRequestPayload['sortBy'];
                sortAscending: boolean;
                atbType: number;
                stateOfCustomers: string;
            }>
        ) => {
            state.sortBy = action.payload.sortBy;
            state.sortAscending = action.payload.sortAscending;
            state.atbType = action.payload.atbType;
            state.stateOfCustomers = action.payload.stateOfCustomers;
        },
        updateCustomersTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.tableFilter = action.payload;
        },
        clearCustomersStateAllTableFiltersAction: (state) => {
            state.filters = { ...initialState.filters };
            state.sortBy = initialState.sortBy;
            state.sortAscending = initialState.sortAscending;
            state.tableFilter = initialState.tableFilter;
        },
        clearCustomersStateDataAction: (state) => {
            Object.assign(state, initialState);
        },
        saveCustomerUpdateRequestAction: (
            state,
            _action: PayloadAction<SaveCustomerUpdateRequestPayload>
        ) => {
            state.saveCustomerDataLoading = true;
        },
        saveOrganisationCustomerUpdateRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    payload: SaveCustomerUpdateRequestPayload,
                    region: string
                }>
            ) => {
                state.saveCustomerDataLoading = true;
            },
            prepare: (
                payload: SaveCustomerUpdateRequestPayload,
                region: string
            ) => {
                return {
                    payload: {
                        payload,
                        region
                    }
                }
            }
        },
        saveCustomerUpdateResponseAction: (state) => {
            state.saveCustomerDataLoading = false;
        },
        bulkSaveCustomerUpdateRequestAction: (
            state,
            _action: PayloadAction<BulkSaveCustomerUpdateRequestPayload>
        ) => {
            state.saveCustomerDataLoading = true;
        },
        bulkSaveCustomerUpdateResponseAction: (state) => {
            state.saveCustomerDataLoading = false;
        },
        setCustomerSelectedIdRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    customerId: string | null;
                    callback?: () => void;
                }>
            ) => {
                state.activeData.selectedId = initialState.activeData.selectedId;
            },
            prepare: (
                customerId: string | null,
                callback?: () => void
            ) => {
                return {
                    payload: {
                        customerId,
                        callback
                    }
                }
            }
        },
        setCustomerSelectedIdSuccessAction: (
            state,
            action: PayloadAction<string | null>
        ) => {
            state.activeData.selectedId = action.payload;
        },
        getCustomerDataRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    customerId: string,
                    companyId: string, 
                    isUsingCloudImportType: boolean, 
                    conversionCurrency: number,
                    callback?: () => void
                }>
            ) => {
                state.activeData.record = { ...initialState.activeData.record };
                state.activeData.loading = true;
            },
            prepare: (
                customerId: string,
                companyId: string,
                isUsingCloudImportType: boolean,
                conversionCurrency: number,
                callback?: () => void
            ) => {
                return {
                    payload: {
                        customerId,
                        companyId,
                        isUsingCloudImportType,
                        conversionCurrency,
                        callback
                    }
                }
            }
        },
        getOrganisationCustomerDataRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    customerId: string,
                    companyId: string,
                    region: string,
                    isUsingCloudImportType: boolean,
                    conversionCurrency: number,
                    stateOfCustomers: string 
                }>
            ) => {
                state.activeData.record = { ...initialState.activeData.record };
                state.activeData.loading = true;
            },
            prepare: (
                customerId: string,
                companyId: string,
                region: string,
                isUsingCloudImportType: boolean,
                conversionCurrency: number,
                stateOfCustomers: string 
            ) => {
                return {
                    payload: {
                        customerId,
                        companyId,
                        region,
                        isUsingCloudImportType,
                        conversionCurrency,
                        stateOfCustomers
                    }
                }
            }
        },
        getCustomerDataSuccessAction: (
            state,
            action: PayloadAction<{ record: {} }>
        ) => {
            state.activeData.record = action.payload.record;
            state.activeData.loading = false;
            state.activeData.errorMessages = [];
        },
        getCustomerDataErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.record = initialState.activeData.record;
            state.activeData.loading = false;
            state.activeData.errorMessages = action.payload;
        },
        clearCustomerDataAction: (state) => {
            state.activeData = initialState.activeData;
        },
        getOrganisationCustomerInvoicesRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    payload: GetInvoicesRequestPayload;
                    region: string
                }>
            ) => {
                state.activeData.invoices.loading = true;
            },
            prepare: (
                payload: GetInvoicesRequestPayload,
                region: string
            ) => {
                return {
                    payload: {
                        payload,
                        region
                    }
                }
            }
        },
        getCustomerInvoicesRequestAction: (
            state,
            _action: PayloadAction<GetInvoicesRequestPayload>
        ) => {
            state.activeData.invoices.loading = true;
        },
        getCustomerInvoicesSuccessAction: (
            state,
            action: PayloadAction<{
                data: Invoice[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.activeData.invoices.data = action.payload.data;
            } else {
                const existingData = state.activeData.invoices.data || [];
                state.activeData.invoices.data = uniqWith([...existingData, ...action.payload.data], isEqual);
            }

            state.activeData.invoices.loading = false;
            state.activeData.invoices.pageData = action.payload.pageData;
            state.activeData.invoices.errorMessages = [];
        },
        getCustomerInvoicesErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.invoices.loading = false;
            state.activeData.invoices.data = initialState.activeData.invoices.data;
            state.activeData.invoices.errorMessages = action.payload;
        },
        updateCustomerInvoicesFiltersAction: (
            state,
            action: PayloadAction<GetInvoicesRequestPayload['filters']>
        ) => {
            Object.assign(state.activeData.invoices.filters, action.payload);
        },
        updateCustomerInvoicesSortByAndStateAction: (
            state,
            action: PayloadAction<{
                sortBy: GetInvoicesRequestPayload['sortBy'];
                sortAscending: boolean;
                invoiceState: string;
            }>
        ) => {
            state.activeData.invoices.sortBy = action.payload.sortBy;
            state.activeData.invoices.sortAscending = action.payload.sortAscending;
            state.activeData.invoices.invoiceState = action.payload.invoiceState;
        },
        updateCustomerInvoicesTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeData.invoices.invoiceState = action.payload;
        },
        getCustomerCreditsRequestAction: (
            state,
            _action: PayloadAction<GetCreditsRequestPayload>
        ) => {
            state.activeData.credits.loading = true;
        },
        getOrganisationCustomerCreditsRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    payload: GetCreditsRequestPayload,
                    region: string
                }>
            ) => {
                state.activeData.credits.loading = true;
            },
            prepare: (
                payload: GetCreditsRequestPayload,
                region: string
            ) => {
                return {
                    payload: {
                        payload,
                        region
                    }
                }
            }
        },
        getCustomerCreditsSuccessAction: (
            state,
            action: PayloadAction<{
                data: Credit[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.activeData.credits.data = action.payload.data;
            } else {
                const existingData = state.activeData.credits.data || [];
                state.activeData.credits.data = uniqWith([...existingData, ...action.payload.data], isEqual);
            }

            state.activeData.credits.loading = false;
            state.activeData.credits.pageData = action.payload.pageData;
            state.activeData.credits.errorMessages = [];
        },
        getCustomerCreditsErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.credits.loading = false;
            state.activeData.credits.data = [];
            state.activeData.credits.errorMessages = action.payload;
        },
        updateCustomerCreditsFiltersAction: (
            state,
            action: PayloadAction<GetCreditsRequestPayload['filters']>
        ) => {
            state.activeData.credits.filters = action.payload;
        },
        updateCustomerCreditsSortByAndStateAction: (
            state,
            action: PayloadAction<{
                sortBy: GetCreditsRequestPayload['sortBy'];
                sortAscending: boolean;
                creditState: string;
            }>
        ) => {
            state.activeData.credits.sortBy = action.payload.sortBy;
            state.activeData.credits.sortAscending = action.payload.sortAscending;
            state.activeData.credits.creditState = action.payload.creditState;
        },
        updateCustomerCreditsTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeData.credits.creditState = action.payload;
        },
        getCustomerPaymentsRequestAction: (
            state,
            _action: PayloadAction<GetPaymentsRequestPayload>
        ) => {
            state.activeData.payments.loading = true;
        },
        getOrganisationCustomerPaymentsRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    payload: GetPaymentsRequestPayload,
                    region: string
                }>
            ) => {
                state.activeData.payments.loading = true;
            },
            prepare: (
                payload: GetPaymentsRequestPayload,
                region: string
            ) => {
                return {
                    payload: {
                        payload,
                        region
                    }
                }
            }
        },
        getCustomerPaymentsSuccessAction: (
            state,
            action: PayloadAction<{
                data: Payment[];
                pageData: PageData;
            }>
        ) => {
             if (action.payload.pageData.currentPage === 0) {
                 state.activeData.payments.data = action.payload.data;
             } else {
                 const existingData = state.activeData.payments.data || [];
                 state.activeData.payments.data = uniqWith([...existingData, ...action.payload.data], isEqual);
             }

            state.activeData.payments.loading = false;
            state.activeData.payments.pageData = action.payload.pageData;
            state.activeData.payments.errorMessages = [];
        },
        getCustomerPaymentsErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.payments.loading = false;
            state.activeData.payments.data = [];
            state.activeData.payments.errorMessages = action.payload;
        },
        updateCustomerPaymentsFiltersAction: (
            state,
            action: PayloadAction<GetPaymentsRequestPayload['filters']>
        ) => {
            Object.assign(state.activeData.payments.filters, action.payload);
        },
        updateCustomerPaymentsSortByAction: (
            state,
            action: PayloadAction<{
                sortBy: GetPaymentsRequestPayload['sortBy'];
                sortAscending: boolean;
            }>
        ) => {
            state.activeData.payments.sortBy = action.payload.sortBy;
            state.activeData.payments.sortAscending = action.payload.sortAscending;
        },
        updateCustomerPaymentsTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeData.payments.paymentState = action.payload;
        },
        getCustomerConversationRequestAction: (
            state,
            _action: PayloadAction<GetConversationRequestPayload>
        ) => {
            state.activeData.conversation.loading = true;
        },
        getOrganisationCustomerConversationRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    payload: GetConversationRequestPayload,
                    region: string,
                    companyIds: string[]
                }>
            ) => {
                state.activeData.conversation.loading = true;
            },
            prepare: (
                payload: GetConversationRequestPayload,
                region: string,
                companyIds: string[]
            ) => {
                return {
                    payload: {
                        payload,
                        region,
                        companyIds
                    }
                }
            }
        },
        getCustomerConversationSuccessAction: (
            state,
            action: PayloadAction<{
                data: Conversation[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.activeData.conversation.data = action.payload.data;
            } else {
                const existingData = state.activeData.conversation.data || [];
                state.activeData.conversation.data = uniqWith([...existingData, ...action.payload.data], isEqual);
            }

            state.activeData.conversation.loading = false;
            state.activeData.conversation.pageData = action.payload.pageData;
            state.activeData.conversation.errorMessages = [];
        },
        getCustomerConversationErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.conversation.loading = false;
            state.activeData.conversation.data = [];
            state.activeData.conversation.errorMessages = action.payload;
        },
        updateCustomerConversationFiltersAction: (
            state,
            action: PayloadAction<GetConversationRequestPayload['filters']>
        ) => {
            Object.assign(state.activeData.conversation.filters, action.payload);
        },
        updateCustomerConversationTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeData.conversation.conversationTableFilter = action.payload;
        },
        updateCustomerConversationSortByAction: (
            state,
            action: PayloadAction<{
                sortBy: GetConversationRequestPayload['sortBy'];
                sortAscending: boolean;
            }>
        ) => {
            state.activeData.conversation.sortBy = action.payload.sortBy;
            state.activeData.conversation.sortAscending = action.payload.sortAscending;
        },
        getChildCustomersForOrganisationRequestAction: (
            state,
            _action: PayloadAction<GetCustomersRequestPayload>
        ) => {
            state.activeData.children.loading = true;
        },
        getChildCustomersRequestAction: (
            state,
            _action: PayloadAction<any>
        ) => {
            state.activeData.children.loading = true;
        },
        getChildCustomersSuccessAction: (
            state,
            action: PayloadAction<{
                data: Customer[];
                pageData: PageData;
                mergeData?: boolean;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.activeData.children.data = action.payload.data;
            } else {
                const existingData = state.activeData.children.data || [];
                state.activeData.children.data = uniqWith([...existingData, ...action.payload.data], isEqual);
            }

            state.activeData.children.loading = false;
            state.activeData.children.pageData = action.payload.pageData;
            state.activeData.children.errorMessages = initialState.activeData.children.errorMessages;
        },
        getChildCustomersErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.children.loading = false;
            state.activeData.children.data = initialState.activeData.children.data;
            state.activeData.children.errorMessages = action.payload;
        },
        updateChildCustomersFiltersAction: (
            state,
            action: PayloadAction<GetCustomersRequestPayload['filters']>
        ) => {
            state.activeData.children.filters = action.payload;
        },
        updateChildCustomersSortByAndStateAction: (
            state,
            action: PayloadAction<{
                sortBy: GetCustomersRequestPayload['sortBy'];
                sortAscending: boolean;
                atbType: number;
            }>
        ) => {
            state.activeData.children.sortBy = action.payload.sortBy;
            state.activeData.children.sortAscending = action.payload.sortAscending;
        },
        updateChildCustomersTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.tableFilter = action.payload;
        },
        setParentCustomerSelectedIdRequestAction: {
            reducer: (
                state,
                action: PayloadAction<{
                    customerId: string | null,
                    callback: () => void
                }>
            ) => {
                state.parentActiveData.selectedId = action.payload.customerId;
            },
            prepare: (
                customerId: string | null,
                callback: () => void
            ) => {
                return {
                    payload: {
                        customerId,
                        callback
                    }
                }
            }
        },
        setParentCustomerSelectedIdSuccessAction: (
            state,
            action: PayloadAction<string | null>
        ) => {
            state.parentActiveData.selectedId = action.payload;
        },
        getParentCustomerDataRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    customerId: string,
                    companyId: string,
                    isUsingCloudImportType: boolean,
                    conversionCurrency: number
                }>
            ) => {
                state.parentActiveData.record = { ...initialState.parentActiveData.record };
                state.parentActiveData.loading = true;
            },
            prepare: (
                customerId: string,
                companyId: string,
                isUsingCloudImportType: boolean,
                conversionCurrency: number,
            ) => {
                return {
                    payload: {
                        customerId,
                        companyId,
                        isUsingCloudImportType,
                        conversionCurrency,
                    }
                }
            }
        },
        getOrganisationParentCustomerDataRequestAction: (
            state,
            _action: PayloadAction<{
                customerId: string,
                isUsingCloudImportType: boolean
            }>
        ) => {
            state.parentActiveData.record = initialState.parentActiveData.record;
            state.parentActiveData.loading = true;
        },
        getParentCustomerDataSuccessAction: (
            state,
            action: PayloadAction<{ record: {} }>
        ) => {
            state.parentActiveData.record = action.payload.record;
            state.parentActiveData.loading = false;
            state.parentActiveData.errorMessages = initialState.parentActiveData.errorMessages;
        },
        getParentCustomerDataErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.parentActiveData.record = initialState.parentActiveData.record;
            state.parentActiveData.loading = false;
            state.parentActiveData.errorMessages = action.payload;
        },
        clearParentCustomerDataAction: (
            state,
            _action: PayloadAction
        ) => {
            state.parentActiveData = initialState.parentActiveData;
        },
        customerAssignUserRequestAction: (
            state,
            _action: PayloadAction<CustomerAssignUserRequestPayload>
        ) => {
            state.activeData.loading = true;
        },
    }
});

const { actions, reducer } = customerSlice;

// Actions
export const {
    getCustomersForOrganisationRequestAction,
    getCustomersRequestAction,
    getCustomersForPaymentPlansRequestAction,
    getCustomersSuccessAction,
    getCustomersErrorAction,
    updateCustomersFiltersAction,
    updateCustomersSortByAndStateAction,
    updateCustomersTableFilterAction,
    clearCustomersStateAllTableFiltersAction,
    clearCustomersStateDataAction,
    saveCustomerUpdateRequestAction,
    saveOrganisationCustomerUpdateRequestAction,
    saveCustomerUpdateResponseAction,
    bulkSaveCustomerUpdateRequestAction,
    bulkSaveCustomerUpdateResponseAction,
    setCustomerSelectedIdRequestAction,
    setCustomerSelectedIdSuccessAction,
    getCustomerDataRequestAction,
    getOrganisationCustomerDataRequestAction,
    getCustomerDataSuccessAction,
    getCustomerDataErrorAction,
    clearCustomerDataAction,
    getOrganisationCustomerInvoicesRequestAction,
    getCustomerInvoicesRequestAction,
    getCustomerInvoicesSuccessAction,
    getCustomerInvoicesErrorAction,
    updateCustomerInvoicesFiltersAction,
    updateCustomerInvoicesSortByAndStateAction,
    updateCustomerInvoicesTableFilterAction,
    getCustomerCreditsRequestAction,
    getOrganisationCustomerCreditsRequestAction,
    getCustomerCreditsSuccessAction,
    getCustomerCreditsErrorAction,
    updateCustomerCreditsFiltersAction,
    updateCustomerCreditsSortByAndStateAction,
    updateCustomerCreditsTableFilterAction,
    getCustomerPaymentsRequestAction,
    getOrganisationCustomerPaymentsRequestAction,
    getCustomerPaymentsSuccessAction,
    getCustomerPaymentsErrorAction,
    updateCustomerPaymentsFiltersAction,
    updateCustomerPaymentsSortByAction,
    updateCustomerPaymentsTableFilterAction,
    getCustomerConversationRequestAction,
    getOrganisationCustomerConversationRequestAction,
    getCustomerConversationSuccessAction,
    getCustomerConversationErrorAction,
    updateCustomerConversationFiltersAction,
    updateCustomerConversationTableFilterAction,
    updateCustomerConversationSortByAction,
    getChildCustomersForOrganisationRequestAction,
    getChildCustomersRequestAction,
    getChildCustomersSuccessAction,
    getChildCustomersErrorAction,
    updateChildCustomersFiltersAction,
    updateChildCustomersSortByAndStateAction,
    updateChildCustomersTableFilterAction,
    setParentCustomerSelectedIdRequestAction,
    setParentCustomerSelectedIdSuccessAction,
    getParentCustomerDataRequestAction,
    getOrganisationParentCustomerDataRequestAction,
    getParentCustomerDataSuccessAction,
    getParentCustomerDataErrorAction,
    clearParentCustomerDataAction,
    customerAssignUserRequestAction
} = actions;

export const customerAddCommentRequestAction = createAction(
    `${actionPrefix}/customerAddCommentRequestAction`,
    function prepare(payload: CustomerAddCommentRequestPayload) {
        return {
            payload
        };
    }
);

export const organisationCustomerAddCommentRequestAction = createAction(
    `${actionPrefix}/organisationCustomerAddCommentRequestAction`,
    function prepare(
        payload: CustomerAddCommentRequestPayload,
        region: string,
        companyIds: string[]
    ) {
        return {
            payload: { payload, region, companyIds }
        };
    }
);

export const customerConversationsFetchedForOrganisationAction = createAction(
    `${actionPrefix}/customerConversationsFetchedForOrganisationAction`,
    function prepare(payload: ConversationLinesFetchedForOrganisationPayload) {
        return {
            payload
        };
    }
);

export const customerSendInformationRequestAction = createAction(
    `${actionPrefix}/customerSendInformationRequestAction`,
    function prepare(payload: CustomerSendInformationRequestPayload) {
        return {
            payload
        };
    }
);

export const customerCreditsFetchedForOrganisationAction = createAction(
    `${actionPrefix}/customerCreditsFetchedForOrganisationAction`,
    function prepare(payload: CreditsFetchedForOrganisationPayload) {
        return {
            payload
        };
    }
);

export const customerInvoicesFetchedForOrganisationAction = createAction(
    `${actionPrefix}/customerInvoicesFetchedForOrganisationAction`,
    function prepare(payload: InvoicesFetchedForOrganisationPayload) {
        return {
            payload
        };
    }
);

export const customerPaymentsFetchedForOrganisationAction = createAction(
    `${actionPrefix}/customerPaymentsFetchedForOrganisationAction`,
    function prepare(payload: PaymentsFetchedForOrganisationPayload) {
        return {
            payload
        };
    }
);

export const customersFetchedForOrganisationAction = createAction(
    `${actionPrefix}/customersFetchedForOrganisationAction`,
    function prepare(payload: CustomersFetchedForOrganisationPayload) {
        return {
            payload
        };
    }
);

export const getCustomerFirstLetterValuesForOrganisationRequestAction = createAction(
    `${actionPrefix}/getCustomerFirstLetterValuesForOrganisationRequestAction`,
    function prepare(
        companyIds: string[],
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                companyIds,
                callback
            }
        };
    }
);

export const getCustomerCustomFieldsForCompanyRequestAction = createAction(
    `${actionPrefix}/getCustomerCustomFieldsForCompanyRequestAction`,
    function prepare(
        companyId: string,
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                companyId,
                callback
            }
        };
    }
);

export const getCustomerCustomFieldsForOrganisationRequestAction = createAction(
    `${actionPrefix}/getCustomerCustomFieldsForOrganisationRequestAction`,
    function prepare(
        CompanyIds: string[],
        FieldName?: string[],
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                CompanyIds,
                FieldName,
                callback
            }
        };
    }
);

export const getCustomerFirstLetterValuesRequestAction = createAction(
    `${actionPrefix}/getCustomerFirstLetterValuesRequestAction`,
    function prepare(
        companyId: string,
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                companyId,
                callback
            }
        };
    }
);

// Selectors
export const getCustomersState = (state: RootState) => state.customers;
export const getCustomersData = createSelector(
    getCustomersState,
    (customers) => customers.data
)
export const getSaveCustomerDataLoading = createSelector(
    getCustomersState,
    (customers) => customers.saveCustomerDataLoading
);
export const getCustomerData = createSelector(
    getCustomersState,
    (customers) => customers.activeData
);
export const getCustomerActiveDataRecord = createSelector(
    getCustomerData,
    (activeData) => activeData.record
);
export const getCustomerInvoicesData = createSelector(
    getCustomerData,
    (activeData) => activeData.invoices
);
export const getCustomerSelectedId = createSelector(
    getCustomerData,
    (activeData) => activeData.selectedId
);
export const getCustomersPageData = createSelector(
    getCustomersState,
    (customers) => customers.pageData
);
export const getCompanyIdByCustomerSelectedId = createSelector(
    [getCustomerSelectedId, getCustomersData],
    (selectedId, customers) => {
        const customer = customers.find((customer) => customer.Id === selectedId) as any;
        return customer ? customer.CompanyId : undefined;
    }
);
export const getCustomerActiveDataConversation = createSelector(
    getCustomerData,
    (activeData) => activeData.conversation
);
export const getCustomerActiveLoading = createSelector(
    getCustomerData,
    (activeData) => activeData.loading
);
export const getParentCustomerData = createSelector(
    getCustomersState,
    (customers) => customers.parentActiveData
);
export const getParentCustomerSelectedId = createSelector(
    getParentCustomerData,
    (activeData) => activeData.selectedId
);
export const getParentCustomerActiveLoading = createSelector(
    getParentCustomerData,
    (activeData) => activeData.loading
);

// Reducer
export default reducer;